import React from 'react';
import { Select, MenuItem, FormControl, InputLabel, FormHelperText, Box } from '@mui/material';

export type DropdownOption = {
    value: string;
    label: string;
};

export type DropdownProps = {
    options: DropdownOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    title?: string;
    width?: string | number;
    height?: string | number;
    borderRadius?: string | number;
    disabled?: boolean;
    isPrimary?: boolean;
    isBordered?: boolean;
    error?: boolean;
    helperText?: string;
    renderOption?: (option: DropdownOption, index: number) => React.ReactNode;
    lastOption?: React.ReactNode;
    InputProps?: React.HTMLAttributes<HTMLDivElement> & {
        startAdornment?: React.ReactNode;
        style?: React.CSSProperties;
    };
};

const Dropdown: React.FC<DropdownProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    title,
    width = '100%',
    height = 'auto',
    disabled = false,
    isPrimary = false,
    isBordered = true,
    borderRadius,
    error = false,
    helperText,
    renderOption,
    lastOption,
    InputProps = {},
}) => {
    const [dropdownOptions, setDropdownOptions] = React.useState<DropdownOption[]>(options);

    React.useEffect(() => {
        setDropdownOptions(options);
    }, [options]);

    return (
        <FormControl
            fullWidth
            disabled={disabled}
            error={error}
            sx={{ 
                width, 
                minHeight: 'unset',
                borderRadius: borderRadius,
                ...(InputProps.style || {}),
            }}
            variant="outlined"
        >
            {title && value && <InputLabel shrink>{title}</InputLabel>}
            <Select
                value={value}
                onChange={e => onChange(e.target.value as string)}
                renderValue={(selected) => {
                    if (!selected) return placeholder;
                    const selectedOption = dropdownOptions.find(opt => opt.value === selected);
                    return selectedOption ? selectedOption.label : selected;
                }}
                sx={{
                    color: value ? isPrimary ? 'var(--color-primary)' : 'var(--color-default-text)' : 'var(--color-text-muted)',
                    fontSize: InputProps.style?.fontSize || 15.5,
                    fontWeight: InputProps.style?.fontWeight || 400,
                    background: 'var(--color-page-background)',
                    width,
                    height,
                    minHeight: 'unset',
                    borderRadius: borderRadius,
                    '& .css-18jp67o-MuiNativeSelect-root-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                        padding: '0 8px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': { 
                        border: isBordered 
                            ? `1px solid ${error ? '#d32f2f' : (isPrimary ? 'var(--color-primary)' : '#ccc')}` 
                            : 'none', 
                        borderRadius: borderRadius
                    },
                }}
                displayEmpty
                startAdornment={InputProps.startAdornment}
                variant="outlined"
                label={value && title ? title : undefined}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 200,
                            padding: 0 
                        },
                    },
                }}
            >
                {placeholder && !value && (
                    <MenuItem value="" disabled sx={{ fontSize: 14, color: 'var(--color-text-muted)' }}>
                        {placeholder}
                    </MenuItem>
                )}
                {dropdownOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                        No options
                    </MenuItem>
                ) : (
                    dropdownOptions.map((opt, index) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
                            {renderOption ? renderOption(opt, index) : opt.label}
                        </MenuItem>
                    ))
                )}
                {lastOption && (
                    <MenuItem value="" sx={{ padding: 0 }}>
                        {lastOption}
                    </MenuItem>
                )}
            </Select>
            {helperText && (
                <FormHelperText>{helperText}</FormHelperText>
            )}
        </FormControl>
    );
};

export default Dropdown;
